"use client"

import * as React from "react"
import { useLocale } from "next-intl"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"

import { cn } from "@/lib/utils"
import { textDirectionForLocale } from "@/lib/locale"
import { Button } from "@/components/ui/button"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  direction: "ltr" | "rtl"
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)
const HORIZONTAL_WHEEL_DELTA_THRESHOLD = 8
const CAROUSEL_EDGE_CONTROL_CLASSES =
  "pointer-events-none absolute z-20 hidden size-11 touch-manipulation rounded-full border-white/30 bg-white/95 text-stone-900 opacity-0 shadow-xl transition-[opacity,background-color,color,scale] duration-200 group-hover/carousel:pointer-events-auto group-hover/carousel:opacity-100 group-focus-within/carousel:pointer-events-auto group-focus-within/carousel:opacity-100 hover:scale-105 hover:bg-white hover:text-stone-950 focus-visible:pointer-events-auto focus-visible:opacity-100 disabled:hidden md:inline-flex"

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const locale = useLocale()
  const direction = opts?.direction ?? textDirectionForLocale(locale)
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
      direction,
    },
    plugins,
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext],
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("reInit", onSelect)
      api?.off("select", onSelect)
    }
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        direction,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("group/carousel relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({
  className,
  viewportClassName,
  onWheel,
  ...props
}: React.ComponentProps<"div"> & { viewportClassName?: string }) {
  const { carouselRef, orientation, api } = useCarousel()

  function handleWheel(event: React.WheelEvent<HTMLDivElement>) {
    onWheel?.(event)
    if (event.defaultPrevented) return
    if (orientation !== "horizontal" || !api) return
    if (
      Math.abs(event.deltaX) <= Math.abs(event.deltaY) ||
      Math.abs(event.deltaX) < HORIZONTAL_WHEEL_DELTA_THRESHOLD
    ) {
      return
    }

    if (event.deltaX > 0 && api.canScrollNext()) {
      event.preventDefault()
      api.scrollNext()
    } else if (event.deltaX < 0 && api.canScrollPrev()) {
      event.preventDefault()
      api.scrollPrev()
    }
  }

  return (
    <div
      ref={carouselRef}
      className={cn("overflow-x-clip overflow-y-visible", viewportClassName)}
      data-slot="carousel-content"
      onWheel={handleWheel}
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className,
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon-sm",
  label = "Previous slide",
  "aria-label": ariaLabelOverride,
  ...props
}: React.ComponentProps<typeof Button> & { label?: string }) {
  const { orientation, direction, scrollPrev, canScrollPrev } = useCarousel()
  const isRtl = orientation === "horizontal" && direction === "rtl"
  // Single source of truth for the button's accessible name. A caller may
  // pass `aria-label` directly OR the `label` prop; pull `aria-label` out
  // of the prop spread so it never silently overrides `label` while the
  // (now-removed) sr-only span uses a different value.
  const accessibleName = ariaLabelOverride ?? label

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      aria-label={accessibleName}
      className={cn(
        CAROUSEL_EDGE_CONTROL_CLASSES,
        orientation === "horizontal"
          ? cn("top-1/2 -translate-y-1/2", isRtl ? "right-3" : "left-3")
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
    </Button>
  )
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon-sm",
  label = "Next slide",
  "aria-label": ariaLabelOverride,
  ...props
}: React.ComponentProps<typeof Button> & { label?: string }) {
  const { orientation, direction, scrollNext, canScrollNext } = useCarousel()
  const isRtl = orientation === "horizontal" && direction === "rtl"
  const accessibleName = ariaLabelOverride ?? label

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      aria-label={accessibleName}
      className={cn(
        CAROUSEL_EDGE_CONTROL_CLASSES,
        orientation === "horizontal"
          ? cn("top-1/2 -translate-y-1/2", isRtl ? "left-3" : "right-3")
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      {isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
    </Button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel,
}
